import { useRef, useCallback, useEffect, useState } from 'react'
import type { Workflow, Participant, SelectedComponent, PanOffset, ContextMenuState } from './types'
import { ZOOM_SPEED, DOUBLE_TAP_DELAY, LONG_PRESS_DURATION } from './constants'
import { findComponentAtPosition, findActivityAtPosition, isOnActivityEdge, isActivity, getActivityWidth } from './geometry'
import ActivityNode from './ActivityNode'
import TransitionLine from './TransitionLine'

interface Props {
  workflow: Workflow
  participants: Participant[]
  selectedId: string | null
  selectedType: 'activity' | 'transition' | null
  transitionMode: boolean
  transitionStartId: string | null
  zoomLevel: number
  panOffset: PanOffset
  theme: 'dark' | 'light'
  onSelect: (component: SelectedComponent) => void
  onMoveActivity: (id: string, x: number, y: number) => void
  onSaveState: () => void
  onZoomToward: (zoom: number, mx: number, my: number) => void
  onPanChange: (offset: PanOffset) => void
  onTransitionModeClick: (activityId: string) => void
  onCancelTransitionMode: () => void
  onDoubleClick: (component: SelectedComponent) => void
  onContextMenu: (state: ContextMenuState) => void
  onEdgeDragTransition: (fromId: string, toId: string) => void
}

export default function WorkflowCanvas({
  workflow,
  participants,
  selectedId,
  selectedType,
  transitionMode,
  transitionStartId,
  zoomLevel,
  panOffset,
  theme,
  onSelect,
  onMoveActivity,
  onSaveState,
  onZoomToward,
  onPanChange,
  onTransitionModeClick,
  onCancelTransitionMode,
  onDoubleClick,
  onContextMenu,
  onEdgeDragTransition,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null)

  // Drag state
  const draggingRef = useRef(false)
  const dragActivityIdRef = useRef<string | null>(null)
  const dragOffsetRef = useRef({ x: 0, y: 0 })

  // Pan state
  const panningRef = useRef(false)
  const panStartRef = useRef({ x: 0, y: 0 })

  // Edge-drag transition state
  const edgeDraggingRef = useRef(false)
  const edgeDragFromIdRef = useRef<string | null>(null)

  // Transition preview line (for both transition mode and edge-drag)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)

  // Double-tap detection
  const lastTapRef = useRef(0)

  // Long press detection
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressStartRef = useRef({ x: 0, y: 0 })

  /** Convert client coordinates to workflow coordinates. */
  const toWorkflowCoords = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    const rect = svg.getBoundingClientRect()
    return {
      x: (clientX - rect.left - panOffset.x) / zoomLevel,
      y: (clientY - rect.top - panOffset.y) / zoomLevel,
    }
  }, [panOffset, zoomLevel])

  // ---- Mouse handlers ----

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 2) return // context menu handled separately

    const { x, y } = toWorkflowCoords(e.clientX, e.clientY)

    // Middle button or Alt+click → always pan
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      panningRef.current = true
      panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y }
      return
    }

    const component = findComponentAtPosition(x, y, workflow)

    if (transitionMode) {
      if (component && isActivity(component)) {
        onTransitionModeClick(component.id)
      } else {
        onCancelTransitionMode()
      }
      return
    }

    // Check if clicking on edge of an activity → start edge-drag transition
    if (component && isActivity(component) && isOnActivityEdge(x, y, component)) {
      edgeDraggingRef.current = true
      edgeDragFromIdRef.current = component.id
      onSelect(component)
      return
    }

    onSelect(component)

    if (component && isActivity(component)) {
      // Drag activity
      draggingRef.current = true
      dragActivityIdRef.current = component.id
      dragOffsetRef.current = { x: x - component.x, y: y - component.y }
    } else if (!component) {
      // Left-click on empty space → pan
      panningRef.current = true
      panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y }
    }
  }, [toWorkflowCoords, workflow, transitionMode, panOffset, onSelect, onTransitionModeClick, onCancelTransitionMode])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (panningRef.current) {
      onPanChange({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y,
      })
      return
    }

    if (draggingRef.current && dragActivityIdRef.current) {
      const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
      onMoveActivity(
        dragActivityIdRef.current,
        x - dragOffsetRef.current.x,
        y - dragOffsetRef.current.y,
      )
      return
    }

    // Edge-drag preview line
    if (edgeDraggingRef.current && edgeDragFromIdRef.current) {
      const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
      setMousePos({ x, y })
      return
    }

    // Transition mode preview line
    if (transitionMode && transitionStartId) {
      const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
      setMousePos({ x, y })
    } else if (!edgeDraggingRef.current) {
      setMousePos(null)
    }
  }, [toWorkflowCoords, transitionMode, transitionStartId, onPanChange, onMoveActivity])

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    // Handle edge-drag release
    if (edgeDraggingRef.current && edgeDragFromIdRef.current) {
      const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
      const targetActivity = findActivityAtPosition(x, y, workflow)
      if (targetActivity && targetActivity.id !== edgeDragFromIdRef.current) {
        onEdgeDragTransition(edgeDragFromIdRef.current, targetActivity.id)
      }
      edgeDraggingRef.current = false
      edgeDragFromIdRef.current = null
      setMousePos(null)
      return
    }

    if (draggingRef.current) {
      onSaveState()
    }
    draggingRef.current = false
    dragActivityIdRef.current = null
    panningRef.current = false
  }, [toWorkflowCoords, workflow, onSaveState, onEdgeDragTransition])

  const handleMouseLeave = useCallback(() => {
    draggingRef.current = false
    dragActivityIdRef.current = null
    panningRef.current = false
    edgeDraggingRef.current = false
    edgeDragFromIdRef.current = null
    setMousePos(null)
  }, [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    const direction = e.deltaY > 0 ? -1 : 1
    onZoomToward(zoomLevel + direction * ZOOM_SPEED, mx, my)
  }, [zoomLevel, onZoomToward])

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
    const component = findComponentAtPosition(x, y, workflow)
    if (component) onDoubleClick(component)
  }, [toWorkflowCoords, workflow, onDoubleClick])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const { x, y } = toWorkflowCoords(e.clientX, e.clientY)
    const component = findComponentAtPosition(x, y, workflow)
    onContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      activity: isActivity(component) ? component : null,
    })
  }, [toWorkflowCoords, workflow, onContextMenu])

  // ---- Touch handlers ----

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return
    const touch = e.touches[0]
    const { x, y } = toWorkflowCoords(touch.clientX, touch.clientY)
    const now = Date.now()

    // Long press detection
    longPressStartRef.current = { x: touch.clientX, y: touch.clientY }
    longPressTimeoutRef.current = setTimeout(() => {
      const component = findComponentAtPosition(x, y, workflow)
      onContextMenu({
        visible: true,
        x: touch.clientX,
        y: touch.clientY,
        activity: isActivity(component) ? component : null,
      })
      longPressTimeoutRef.current = null
    }, LONG_PRESS_DURATION)

    // Double-tap detection
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      lastTapRef.current = 0
      const component = findComponentAtPosition(x, y, workflow)
      if (component) onDoubleClick(component)
      return
    }
    lastTapRef.current = now

    const component = findComponentAtPosition(x, y, workflow)

    if (transitionMode) {
      if (component && isActivity(component)) {
        onTransitionModeClick(component.id)
      } else {
        onCancelTransitionMode()
      }
      return
    }

    onSelect(component)

    if (component && isActivity(component)) {
      draggingRef.current = true
      dragActivityIdRef.current = component.id
      dragOffsetRef.current = { x: x - component.x, y: y - component.y }
    } else {
      panningRef.current = true
      panStartRef.current = { x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y }
    }
  }, [toWorkflowCoords, workflow, transitionMode, panOffset, onSelect, onTransitionModeClick, onCancelTransitionMode, onDoubleClick, onContextMenu])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return
    const touch = e.touches[0]

    // Cancel long press on move
    if (longPressTimeoutRef.current) {
      const dx = Math.abs(touch.clientX - longPressStartRef.current.x)
      const dy = Math.abs(touch.clientY - longPressStartRef.current.y)
      if (dx > 10 || dy > 10) {
        clearTimeout(longPressTimeoutRef.current)
        longPressTimeoutRef.current = null
      }
    }

    if (draggingRef.current && dragActivityIdRef.current) {
      const { x, y } = toWorkflowCoords(touch.clientX, touch.clientY)
      onMoveActivity(
        dragActivityIdRef.current,
        x - dragOffsetRef.current.x,
        y - dragOffsetRef.current.y,
      )
    } else if (panningRef.current) {
      onPanChange({
        x: touch.clientX - panStartRef.current.x,
        y: touch.clientY - panStartRef.current.y,
      })
    }
  }, [toWorkflowCoords, onMoveActivity, onPanChange])

  const handleTouchEnd = useCallback(() => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current)
      longPressTimeoutRef.current = null
    }
    if (draggingRef.current) onSaveState()
    draggingRef.current = false
    dragActivityIdRef.current = null
    panningRef.current = false
  }, [onSaveState])

  // Cleanup long press timeout
  useEffect(() => {
    return () => {
      if (longPressTimeoutRef.current) clearTimeout(longPressTimeoutRef.current)
    }
  }, [])

  // Resolve the source activity for preview line (transition mode or edge-drag)
  const previewSourceActivity = transitionStartId
    ? workflow.activities.find(a => a.id === transitionStartId)
    : edgeDragFromIdRef.current
      ? workflow.activities.find(a => a.id === edgeDragFromIdRef.current)
      : null

  // Cursor style
  const cursor = transitionMode
    ? 'crosshair'
    : edgeDraggingRef.current
      ? 'crosshair'
      : draggingRef.current
        ? 'move'
        : panningRef.current
          ? 'grabbing'
          : 'grab'

  return (
    <svg
      ref={svgRef}
      style={{
        width: '100%',
        height: '100%',
        cursor,
        background: theme === 'light' ? '#f5f5f5' : 'var(--color-bg)',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <g transform={`translate(${panOffset.x}, ${panOffset.y}) scale(${zoomLevel})`}>
        {/* Transitions (rendered below activities) */}
        {workflow.transitions.map(t => (
          <TransitionLine
            key={t.id}
            transition={t}
            workflow={workflow}
            isSelected={selectedType === 'transition' && selectedId === t.id}
            theme={theme}
          />
        ))}

        {/* Activities */}
        {workflow.activities.map(a => (
          <ActivityNode
            key={a.id}
            activity={a}
            isSelected={selectedType === 'activity' && selectedId === a.id}
            isTransitionStart={transitionStartId === a.id || edgeDragFromIdRef.current === a.id}
            participants={participants}
            theme={theme}
          />
        ))}

        {/* Transition preview dotted line */}
        {previewSourceActivity && mousePos && (
          <line
            x1={previewSourceActivity.x + getActivityWidth(previewSourceActivity)}
            y1={previewSourceActivity.y + previewSourceActivity.height / 2}
            x2={mousePos.x}
            y2={mousePos.y}
            stroke={theme === 'light' ? '#666' : '#aaa'}
            strokeWidth={2}
            strokeDasharray="6,4"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </g>
    </svg>
  )
}
